"use client";
import { type Atom, useAtomValue } from "jotai";

/**
 * Renders the value of an atom using a render function.
 * This function can use hooks like useMemo etc within
 * @example
 * ```tsx
 * <AtomValue atom={isEnabledAtom}>
 *   {(value) => <div data-loc="phosphor-blog/scripts/lib/jotai/AtomValue.tsx:10:23" data-name="div">{value ? "Enabled" : "Disabled"}</div>}
 * </AtomValue>
 * <AtomValue atom={isEnabledAtom}>
 *   {(value) => {
 *     // Use hooks for whatever you need to avoid creating new component functions
 *     const memoizedValue = useMemo(() => value, [value]);
 *     return <div data-loc="phosphor-blog/scripts/lib/jotai/AtomValue.tsx:16:20" data-name="div">{memoizedValue ? "Enabled" : "Disabled"}</div>;
 *   }}
 * }}
 * </AtomValue>
 * ```
 * @param props.atom - The atom to subscribe to.
 * @param props.children - The function to render the value.
 * @returns The rendered value.
 */
export function AtomValue<T>(props: {
  atom: Atom<T>;
  children: (value: Awaited<T>) => React.ReactNode;
}): React.ReactNode;
/**
 * @example
 * ```tsx
 * // Render as text
 * <AtomValue atom={displayNameAtom} />
 * ```
 * @param props.atom - The atom to subscribe to.
 * @returns The rendered value.
 */
export function AtomValue(props: {
  atom: Atom<React.ReactNode | string | number | boolean | null | undefined>;
}): React.ReactNode;
export function AtomValue(props: {
  atom: Atom<any>;
  children?: (value: any) => React.ReactNode;
}) {
  const value = useAtomValue(props.atom);
  return <>{typeof props.children === "function" ? props.children(value) : (value as any)}</>;
}
